import { Check, Filter } from "lucide-react";
import React, { useState } from "react";
import { FilterMode } from "./portfolio/columns/columnTypes";

export type FilterKey =
  | "currency" | "group" | "sector" | "secType"
  | "targetWeightPct" | "targetQty" | "tradeQty" | "tradeValue" | "rawTargetQty"
  | "tradeAction" | "stage" | "thresholdStatus" | "stageStatus" | "drift" | "stageWindow" | "orderId" | "orderStatus"
  | "disposition" | "reason" | "remarks" | "outcome" | "issues" | "status";

interface FilterPopoverProps {
  filterKey: FilterKey;
  options: string[];
  label: string;
  selectedValues: Set<string>;
  isDarkMode: boolean;
  onToggleValue: (filterKey: FilterKey, value: string) => void;
  onSelectAll: (filterKey: FilterKey, values: string[]) => void;
  onClear: (filterKey: FilterKey) => void;
  filterMode?: FilterMode;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  filterKey,
  options,
  label,
  selectedValues,
  isDarkMode,
  onToggleValue,
  onSelectAll,
  onClear,
  filterMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const hasFilter = selectedValues.size > 0;

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setPopoverPosition(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setPopoverPosition(null);
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleButtonClick}
        className={`p-0.5 rounded transition-colors ${
          hasFilter
            ? "text-teal-500"
            : isDarkMode
            ? "text-gray-500 hover:text-gray-300"
            : "text-gray-400 hover:text-gray-600"
        }`}
        title={`Filter by ${label}`}
      >
        <Filter className="w-3 h-3" />
      </button>
      {isOpen && popoverPosition && (
        <>
          {/* Backdrop to close on click outside */}
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          {/* Popover - Fixed positioning */}
          <div
            style={{
              position: "fixed",
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
            className={`z-50 min-w-[160px] rounded-lg border shadow-xl ${
              isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header with actions */}
            <div
              className={`px-3 py-2 border-b text-xs font-medium ${
                isDarkMode ? "border-gray-700 text-gray-300" : "border-gray-100 text-gray-700"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold">{label}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onSelectAll(filterKey, options)}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      isDarkMode
                        ? "hover:bg-gray-700 text-teal-400 hover:text-teal-300"
                        : "hover:bg-gray-100 text-teal-600 hover:text-teal-700"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => onClear(filterKey)}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      isDarkMode
                        ? "hover:bg-gray-700 text-gray-400 hover:text-gray-300"
                        : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            {/* Options */}
            <div className="py-1 max-h-60 overflow-y-auto">
              {options.map(option => {
                const displayLabel = option === "" ? "-" : option;
                return (
                  <div
                    key={option}
                    onClick={() => onToggleValue(filterKey, option)}
                    className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer transition-colors ${
                      isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedValues.has(option)
                          ? "bg-teal-500 border-teal-500"
                          : isDarkMode
                          ? "border-gray-500"
                          : "border-gray-300"
                      }`}
                    >
                      {selectedValues.has(option) && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <span>{displayLabel}</span>
                  </div>
                );
              })}
            </div>
            {/* Footer showing count */}
            {selectedValues.size > 0 && (
              <div
                className={`px-3 py-2 border-t text-xs font-medium ${
                  isDarkMode ? "border-gray-700 text-teal-400" : "border-gray-100 text-teal-600"
                }`}
              >
                {selectedValues.size} of {options.length} selected
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FilterPopover;
